import { UploadableFile } from '~/utils/files/uploadableFile'
import { humanFileSize } from '#shared/utils/fileUtils'

export class FileStorage {
  public file = ref<UploadableFile | null>(null)

  private readonly fileTypes: string[]
  private readonly maxFileSize?: number

  private readonly i18n

  /**
   * @param fileTypes list of MIME types
   * @param maxFileSize in bytes
   */
  constructor(fileTypes?: string | string[], maxFileSize?: number) {
    if (typeof fileTypes === 'string') {
      this.fileTypes = [fileTypes]
    } else {
      this.fileTypes = fileTypes ?? []
    }

    this.maxFileSize = maxFileSize

    this.i18n = useI18n()
  }

  public getFileTypes() {
    return this.fileTypes
  }

  get humanReadableMaxFileSize(): string | undefined {
    return this.maxFileSize ? humanFileSize(this.maxFileSize, true, 0) : undefined
  }

  public isAcceptableFileType(file: File) {
    return this.fileTypes.includes(file.type)
  }

  private filterFileType(fileType: string): boolean {
    return this.fileTypes.length === 0 || this.fileTypes.includes(fileType)
  }

  public addFile(newFile: File, isUploaded: boolean, base64String?: string) {
    if (!this.filterFileType(newFile.type)) {
      const allowedFileTypes = this.fileTypes
        .map((f) => {
          const indexOfSlash = f.indexOf('/')
          return f.slice(indexOfSlash + 1)
        })
        .join(', ')
      addToast().error(
        this.i18n.t('components.molecules.file.file-drop-zone.invalid-file-type', {
          fileTypes: allowedFileTypes
        })
      )
      return
    } else if (this.maxFileSize && newFile.size >= this.maxFileSize) {
      addToast().error(
        this.i18n.t('components.molecules.file.file-drop-zone.file-too-big', {
          fileSize: humanFileSize(newFile.size, true),
          maxFileSize: humanFileSize(this.maxFileSize, true)
        })
      )
      return
    }

    this.file.value = new UploadableFile(newFile, isUploaded, base64String)
  }

  public removeFile() {
    this.file.value = null
  }
}

export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.addEventListener('load', () => {
      const result = reader.result

      if (typeof result !== 'string') {
        reject(new Error('Failed to read file as base64'))
        return
      }

      const [, base64 = ''] = result.split(',')
      resolve(base64)
    })

    reader.addEventListener('error', () => {
      reject(reader.error ?? new Error('Failed to read file'))
    })

    reader.readAsDataURL(file)
  })
}
